import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Shield, Check, ExternalLink, Phone, Zap, AlertTriangle,
  Calendar, BarChart3, Home, Star, Lock, ChevronRight, Layers,
  MessageSquare, BookOpen, ClipboardCheck, Settings2, Gift, TrendingUp, Percent
} from "lucide-react";
import SEO from "../components/SEO";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.07 } } };

const CLEDICI_URL = "https://www.cledici.fr";
const BOOKING_URL = "https://api.leadconnectorhq.com/widget/booking/AK3IH1nwEwNhiDkL4Iak";

const ParticulierFrance = () => {
  return (
    <div className="min-h-screen bg-white font-['Manrope'] overflow-hidden" data-testid="particulier-france-page">
      <SEO
        page="home"
        customTitle="Particulier France — Sécurisez votre location courte durée | HostProtect"
        customDescription="Solution complète pour les propriétaires en France : protection voyageurs, synchronisation Airbnb/Booking, gestion centralisée et optimisation des revenus. Dès 39€/mois."
      />

      {/* ===== HEADER ===== */}
      <header className="fixed w-full z-50 backdrop-blur-xl bg-white/90 border-b border-neutral-200/50">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
            <div className="flex items-center gap-3 md:gap-5">
              <a
                href="tel:+33756868432"
                className="inline-flex items-center gap-1.5 text-sm text-[#19294C] font-medium hover:text-[#00B8E4] transition-colors"
                aria-label="Appelez-nous au 07 56 86 84 32"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden md:inline">07 56 86 84 32</span>
              </a>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <button
                  className="bg-[#00B8E4] text-white text-sm px-5 py-2.5 rounded-full hover:bg-[#00a5ce] transition-colors font-semibold shadow-sm shadow-[#00B8E4]/20"
                  data-testid="header-cta"
                >
                  Prendre rendez-vous
                </button>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative pt-28 md:pt-36 pb-20 md:pb-28 px-6 md:px-12 bg-[#19294C] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#19294C] via-[#1c2f55] to-[#0f1f3d]" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-[#00B8E4]/[0.04] rounded-full blur-[100px] pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="max-w-3xl">
            <motion.div
              className="inline-flex items-center gap-2 bg-[#00B8E4]/10 border border-[#00B8E4]/25 rounded-full px-4 py-1.5 mb-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Zap className="w-3.5 h-3.5 text-[#00B8E4]" />
              <span className="text-xs font-bold text-[#00B8E4] tracking-wide">SOLUTION TOUT-EN-UN</span>
            </motion.div>

            <motion.h1
              className="text-3xl sm:text-4xl lg:text-[3.25rem] font-black text-white font-['Outfit'] tracking-tight leading-[1.1]"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="hero-title"
            >
              Sécurisez et pilotez votre location courte durée
              <span className="bg-gradient-to-r from-[#00B8E4] to-[#2A63C5] bg-clip-text text-transparent"> sans friction</span>
            </motion.h1>

            <motion.p
              className="mt-5 text-base md:text-lg text-white/50 leading-relaxed max-w-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Une solution complète avec protection incluse, automatisation et outils professionnels pour maximiser vos revenus.
            </motion.p>

            {/* Bullet points */}
            <motion.div
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              {[
                "Protection contre les dégradations voyageurs incluse",
                "Alternative à la caution",
                "Synchronisation Airbnb / Booking",
                "Suivi et gestion simplifiée",
              ].map((item, i) => (
                <motion.div key={i} className="flex items-center gap-2.5" variants={fadeUp}>
                  <div className="w-5 h-5 rounded-full bg-[#00B8E4]/20 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-[#00B8E4]" strokeWidth={3} />
                  </div>
                  <span className="text-sm text-white/70">{item}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA */}
            <motion.div
              className="mt-10 flex flex-col sm:flex-row gap-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <button
                  className="bg-gradient-to-r from-[#00B8E4] to-[#2A63C5] text-white px-8 py-4 rounded-full font-semibold hover:opacity-90 transition-all inline-flex items-center gap-2 text-base shadow-lg shadow-[#00B8E4]/25 hover:-translate-y-0.5"
                  data-testid="hero-cta-btn"
                >
                  Prendre rendez-vous
                  <Calendar className="w-4 h-4" />
                </button>
              </a>
              <a href="tel:+33756868432">
                <button className="border border-white/20 text-white px-6 py-4 rounded-full font-medium hover:bg-white/5 transition-all inline-flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4" />
                  07 56 86 84 32
                </button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CONTEXTE ===== */}
      <section className="py-10 md:py-12 px-6 md:px-12 bg-[#F8FAFC] border-b border-slate-200" data-testid="context-section">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-sm md:text-base text-slate-500 leading-relaxed">
            En fonction de votre situation, cette solution est proposée via notre partenaire spécialisé{" "}
            <a href={CLEDICI_URL} target="_blank" rel="noopener noreferrer" className="text-[#00B8E4] font-semibold hover:underline" data-testid="cledici-backlink">
              Cledici
            </a>
            , afin de vous garantir un cadre conforme et une expérience optimale.
          </p>
        </motion.div>
      </section>

      {/* ===== PROBLÈME ===== */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-white" data-testid="problem-section">
        <motion.div
          className="max-w-5xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
        >
          <motion.div className="text-center mb-14" variants={fadeUp}>
            <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-4 py-1.5 mb-5">
              <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
              <span className="text-xs font-bold text-red-600 tracking-wide">LE PROBLÈME</span>
            </div>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#19294C] font-['Outfit'] tracking-tight"
              data-testid="problem-title"
            >
              La location courte durée est rentable…
              <br />
              <span className="text-slate-400">mais pleine de friction</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              { icon: Shield, text: "Dégradations voyageurs difficiles à gérer" },
              { icon: Lock, text: "Caution qui freine les réservations" },
              { icon: AlertTriangle, text: "Plateformes qui protègent mal" },
              { icon: Settings2, text: "Gestion manuelle chronophage" },
              { icon: BarChart3, text: "Revenus pas optimisés" },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="flex items-center gap-3 bg-red-50/60 border border-red-100 rounded-xl p-4"
                variants={fadeUp}
              >
                <item.icon className="w-5 h-5 text-red-400 shrink-0" />
                <p className="text-sm text-[#19294C] font-medium">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ===== SOLUTION ===== */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-[#F8FAFC]" data-testid="solution-section">
        <motion.div
          className="max-w-6xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
        >
          <motion.div className="text-center mb-14" variants={fadeUp}>
            <div className="inline-flex items-center gap-2 bg-[#00B8E4]/10 border border-[#00B8E4]/25 rounded-full px-4 py-1.5 mb-5">
              <Zap className="w-3.5 h-3.5 text-[#00B8E4]" />
              <span className="text-xs font-bold text-[#00B8E4] tracking-wide">LA SOLUTION</span>
            </div>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#19294C] font-['Outfit'] tracking-tight"
              data-testid="solution-title"
            >
              Une solution tout-en-un pour sécuriser
              <br />
              et optimiser votre activité
            </h2>
          </motion.div>

          {/* 3 pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Protection */}
            <motion.div
              className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden hover:shadow-xl transition-all duration-300"
              variants={fadeUp}
            >
              <div className="h-1.5 bg-gradient-to-r from-emerald-400 to-emerald-500" />
              <div className="p-7">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-5">
                  <Shield className="w-6 h-6 text-emerald-500" />
                </div>
                <h3 className="text-lg font-bold text-[#19294C] font-['Outfit'] mb-4">Protection incluse</h3>
                <div className="space-y-3">
                  {[
                    "Couverture contre les dégradations voyageurs",
                    "Alternative à la caution",
                    "Traitement simplifié des incidents",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" strokeWidth={2.5} />
                      <span className="text-sm text-slate-600">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Gestion */}
            <motion.div
              className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden hover:shadow-xl transition-all duration-300"
              variants={fadeUp}
            >
              <div className="h-1.5 bg-gradient-to-r from-amber-400 to-amber-500" />
              <div className="p-7">
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-5">
                  <Calendar className="w-6 h-6 text-amber-500" />
                </div>
                <h3 className="text-lg font-bold text-[#19294C] font-['Outfit'] mb-4">Gestion centralisée</h3>
                <div className="space-y-3">
                  {[
                    "Synchronisation des plateformes",
                    "Calendrier unifié",
                    "Suivi des réservations",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" strokeWidth={2.5} />
                      <span className="text-sm text-slate-600">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Optimisation */}
            <motion.div
              className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden hover:shadow-xl transition-all duration-300"
              variants={fadeUp}
            >
              <div className="h-1.5 bg-gradient-to-r from-blue-400 to-blue-500" />
              <div className="p-7">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-5">
                  <BarChart3 className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-lg font-bold text-[#19294C] font-['Outfit'] mb-4">Outils d'optimisation</h3>
                <div className="space-y-3">
                  {[
                    "Optimisation automatique des prix",
                    "Amélioration du taux d'occupation",
                    "Maximisation du revenu",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" strokeWidth={2.5} />
                      <span className="text-sm text-slate-600">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ===== PRICING ===== */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-white" data-testid="pricing-section">
        <motion.div
          className="max-w-6xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
        >
          <motion.div className="text-center mb-14" variants={fadeUp}>
            <p className="text-xs font-bold text-[#00B8E4] tracking-[0.2em] uppercase mb-3">Tarifs clairs</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#19294C] font-['Outfit'] tracking-tight">
              Choisissez l'offre adaptée à votre activité
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {/* Starter */}
            <motion.div
              className="bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg transition-all duration-300"
              variants={fadeUp}
              data-testid="pricing-starter"
            >
              <p className="text-xs font-bold text-slate-400 tracking-wider uppercase">Starter</p>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-black text-[#19294C] font-['Outfit']">39€</span>
                <span className="text-sm text-slate-400">/mois</span>
              </div>
              <div className="mt-6 space-y-3">
                {[
                  "Protection incluse",
                  "Synchronisation plateformes",
                  "Dashboard gestion",
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 text-[#00B8E4]" strokeWidth={2.5} />
                    <span className="text-sm text-slate-600">{f}</span>
                  </div>
                ))}
              </div>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block mt-7">
                <button className="w-full border border-[#19294C] text-[#19294C] py-3 rounded-full font-semibold text-sm hover:bg-[#19294C] hover:text-white transition-all">
                  Prendre rendez-vous
                </button>
              </a>
            </motion.div>

            {/* Pro — highlighted */}
            <motion.div
              className="bg-[#19294C] rounded-2xl p-7 relative shadow-2xl shadow-[#19294C]/20 ring-2 ring-[#00B8E4]/30"
              variants={fadeUp}
              data-testid="pricing-pro"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00B8E4] text-white text-[10px] font-bold uppercase tracking-wider px-4 py-1 rounded-full">
                Recommandé
              </div>
              <p className="text-xs font-bold text-[#00B8E4] tracking-wider uppercase">Pro</p>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-black text-white font-['Outfit']">59€</span>
                <span className="text-sm text-white/40">/mois</span>
              </div>
              <div className="mt-6 space-y-3">
                {[
                  "Tout Starter inclus",
                  "Guide voyageur digital",
                  "Boutique en ligne",
                  "État des lieux",
                  "Espace prestataire",
                  "Fonctionnalités avancées",
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 text-[#00B8E4]" strokeWidth={2.5} />
                    <span className="text-sm text-white/80">{f}</span>
                  </div>
                ))}
              </div>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block mt-5">
                <button className="w-full bg-gradient-to-r from-[#00B8E4] to-[#2A63C5] text-white py-3 rounded-full font-semibold text-sm hover:opacity-90 transition-all shadow-lg shadow-[#00B8E4]/20">
                  Prendre rendez-vous
                </button>
              </a>
            </motion.div>

            {/* Premium */}
            <motion.div
              className="bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg transition-all duration-300"
              variants={fadeUp}
              data-testid="pricing-premium"
            >
              <p className="text-xs font-bold text-slate-400 tracking-wider uppercase">Premium</p>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-black text-[#19294C] font-['Outfit']">79€</span>
                <span className="text-sm text-slate-400">/mois</span>
              </div>
              <div className="mt-6 space-y-3">
                {[
                  "Tout Pro inclus",
                  "Site web dédié",
                  "Support prioritaire",
                  "Expérience avancée",
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 text-[#00B8E4]" strokeWidth={2.5} />
                    <span className="text-sm text-slate-600">{f}</span>
                  </div>
                ))}
              </div>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block mt-7">
                <button className="w-full border border-[#19294C] text-[#19294C] py-3 rounded-full font-semibold text-sm hover:bg-[#19294C] hover:text-white transition-all">
                  Prendre rendez-vous
                </button>
              </a>
            </motion.div>
          </div>

          {/* Option tarification */}
          <motion.div
            className="mt-8 max-w-5xl mx-auto bg-gradient-to-r from-[#19294C] to-[#1c2f55] rounded-2xl p-6 md:p-8 border border-white/10"
            variants={fadeUp}
            data-testid="pricing-option"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00B8E4]/15 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-6 h-6 text-[#00B8E4]" />
                </div>
                <div>
                  <p className="text-xs font-bold text-[#00B8E4] tracking-wider uppercase mb-1">Option disponible pour chaque offre</p>
                  <h3 className="text-lg font-bold text-white font-['Outfit']">Optimisation des revenus</h3>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 md:gap-6">
                {[
                  "Gestion de la tarification",
                  "Optimisation du taux de remplissage",
                  "Maximisation des revenus",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-[#00B8E4]" strokeWidth={2.5} />
                    <span className="text-sm text-white/70">{item}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white/10 rounded-full px-5 py-2 shrink-0">
                <span className="text-sm font-bold text-white">3% <span className="text-white/50 font-normal">des réservations</span></span>
              </div>
            </div>
          </motion.div>

          {/* Bonus offert */}
          <motion.div
            className="mt-5 max-w-5xl mx-auto bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 md:p-8 border border-amber-200/60"
            variants={fadeUp}
            data-testid="pricing-bonus"
          >
            <div className="flex flex-col md:flex-row items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center shrink-0">
                <Gift className="w-7 h-7 text-amber-600" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <p className="text-xs font-bold text-amber-700 tracking-wider uppercase mb-1">Offert pour toute souscription</p>
                <h3 className="text-lg font-bold text-[#19294C] font-['Outfit']">
                  Audit professionnel de votre annonce Airbnb <span className="text-amber-600">(valeur 179€)</span>
                </h3>
                <p className="text-sm text-slate-500 mt-1">+ 1 heure de consulting stratégique avec un expert location courte durée</p>
              </div>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="shrink-0">
                <button className="bg-[#19294C] text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-[#19294C]/90 transition-all inline-flex items-center gap-2">
                  Réserver mon audit
                  <Calendar className="w-4 h-4" />
                </button>
              </a>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== OPTIONS / UPSELL ===== */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-[#F8FAFC]" data-testid="options-section">
        <motion.div
          className="max-w-5xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
        >
          <motion.div className="text-center mb-10" variants={fadeUp}>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#19294C] font-['Outfit'] tracking-tight">
              Allez plus loin
            </h2>
            <p className="mt-2 text-slate-500 text-sm">Options disponibles pour booster votre activité</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: MessageSquare, title: "Réponses automatiques", desc: "Réponses voyageurs automatisées 24/7" },
              { icon: BookOpen, title: "Guide digital", desc: "Guide personnalisé pour vos voyageurs" },
              { icon: ClipboardCheck, title: "État des lieux numérique", desc: "Avant/après chaque réservation" },
              { icon: Settings2, title: "Automatisation avancée", desc: "Workflows personnalisés" },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-xl border border-slate-200/80 p-5 hover:shadow-md transition-all duration-300 group"
                variants={fadeUp}
              >
                <div className="w-10 h-10 rounded-lg bg-[#19294C]/5 flex items-center justify-center mb-3 group-hover:bg-[#00B8E4]/10 transition-colors">
                  <item.icon className="w-5 h-5 text-[#19294C] group-hover:text-[#00B8E4] transition-colors" />
                </div>
                <h3 className="text-sm font-bold text-[#19294C]">{item.title}</h3>
                <p className="mt-1 text-xs text-slate-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ===== RASSURANCE ===== */}
      <section className="py-14 md:py-16 px-6 md:px-12 bg-white border-b border-slate-200" data-testid="reassurance-section">
        <motion.div
          className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 md:gap-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          {[
            { icon: Star, label: "Utilisée par des professionnels", sub: "de la location courte durée" },
            { icon: Layers, label: "Partenaire spécialisé", sub: "Cledici — expert du secteur" },
            { icon: Shield, label: "Conformité réglementaire", sub: "Cadre légal respecté" },
            { icon: Phone, label: "Accompagnement", sub: "Équipe disponible" },
          ].map((item, i) => (
            <motion.div key={i} className="flex items-center gap-3" variants={fadeUp}>
              <div className="w-9 h-9 rounded-lg bg-[#F8FAFC] flex items-center justify-center shrink-0">
                <item.icon className="w-4 h-4 text-[#19294C]" />
              </div>
              <div>
                <p className="text-xs font-bold text-[#19294C]">{item.label}</p>
                <p className="text-[11px] text-slate-400">{item.sub}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="py-24 md:py-28 px-6 md:px-12 bg-[#19294C] relative overflow-hidden" data-testid="final-cta-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00B8E4]/[0.05] rounded-full blur-[120px] pointer-events-none" />

        <motion.div
          className="max-w-3xl mx-auto text-center relative z-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] tracking-tight"
            data-testid="final-cta-title"
          >
            Optimisez et sécurisez votre activité
            <br />
            <span className="bg-gradient-to-r from-[#00B8E4] to-[#2A63C5] bg-clip-text text-transparent">
              dès maintenant
            </span>
          </h2>
          <p className="mt-4 text-white/40 max-w-lg mx-auto">
            Rejoignez les propriétaires qui ont choisi d'optimiser leurs revenus et de dormir tranquilles.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <button
                className="bg-gradient-to-r from-[#00B8E4] to-[#2A63C5] text-white px-8 py-4 rounded-full font-semibold hover:opacity-90 transition-all inline-flex items-center gap-2 text-base shadow-lg shadow-[#00B8E4]/25 hover:-translate-y-0.5"
                data-testid="final-cta-btn"
              >
                Prendre rendez-vous
                <Calendar className="w-4 h-4" />
              </button>
            </a>
            <a href="tel:+33756868432">
              <button className="border border-white/20 text-white px-8 py-4 rounded-full font-semibold hover:bg-white/5 transition-all inline-flex items-center gap-2 text-base hover:-translate-y-0.5">
                <Phone className="w-4 h-4" />
                07 56 86 84 32
              </button>
            </a>
          </div>

          <p className="mt-6 text-xs text-white/25">
            Solution proposée via{" "}
            <a href={CLEDICI_URL} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#00B8E4] transition-colors underline">
              Cledici.fr
            </a>
            {" "}— Partenaire officiel HostProtect
          </p>
        </motion.div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-10 px-6 md:px-12 bg-[#0B1221] border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img src="/logo-hostprotect.png" alt="HostProtect" className="h-7 w-auto brightness-0 invert opacity-60" />
          </Link>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <a href="tel:+33756868432" className="hover:text-white transition-colors">07 56 86 84 32</a>
            <a href="mailto:contact@hostprotect.io" className="hover:text-white transition-colors">contact@hostprotect.io</a>
            <a href={CLEDICI_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[#00B8E4] transition-colors" data-testid="footer-cledici-link">
              Cledici.fr
            </a>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-6 pt-4 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-slate-600">© {new Date().getFullYear()} HostProtect — PM Hospitality SAS</p>
          <div className="flex items-center gap-4 text-xs text-slate-600">
            <Link to="/conditions-generales-de-vente" className="hover:text-slate-400 transition-colors">CGV</Link>
            <Link to="/politique-de-confidentialite" className="hover:text-slate-400 transition-colors">Confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ParticulierFrance;
